import type { DailyCall } from "@daily-co/daily-js"

const STORAGE_KEY = "jynio.classroom.devices"

export interface StoredDevice {
  deviceId: string
  label: string
}

export interface DevicePreferences {
  camera?: StoredDevice
  microphone?: StoredDevice
  speaker?: StoredDevice
}

export type DeviceKind = keyof DevicePreferences

const KINDS: DeviceKind[] = ["camera", "microphone", "speaker"]

export function loadDevicePreferences(): DevicePreferences {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return {}
    const result: DevicePreferences = {}
    for (const kind of KINDS) {
      const entry = (parsed as Record<string, unknown>)[kind]
      if (
        entry &&
        typeof entry === "object" &&
        typeof (entry as StoredDevice).deviceId === "string" &&
        typeof (entry as StoredDevice).label === "string"
      ) {
        const { deviceId, label } = entry as StoredDevice
        result[kind] = { deviceId, label }
      }
    }
    return result
  } catch {
    return {}
  }
}

export function saveDevicePreference(kind: DeviceKind, device: StoredDevice): void {
  try {
    const next = { ...loadDevicePreferences(), [kind]: device }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Remembering devices is best-effort: private mode or blocked storage
    // must never break the classroom.
  }
}

export const DEVICE_FALLBACK_NAME_KEYS: Record<DeviceKind, string> = {
  camera: "liveSession.devices.cameraFallbackName",
  microphone: "liveSession.devices.microphoneFallbackName",
  speaker: "liveSession.devices.speakersFallbackName",
}

// A user picked a device in a picker: switch to it and remember it. The
// switch is fire-and-forget — a failed switch keeps the previous device and
// the picker simply reflects reality on the next devices update.
export function chooseDevice(
  kind: DeviceKind,
  deviceId: string,
  devices: MediaDeviceInfo[],
  apply: (id: string) => Promise<void> | void,
): void {
  Promise.resolve(apply(deviceId)).catch(() => {})
  const info = devices.find((d) => d.deviceId === deviceId)
  if (info) saveDevicePreference(kind, { deviceId: info.deviceId, label: info.label })
}

// Device IDs aren't stable across sessions in every browser: match by id
// first, then by label, and give up quietly (the browser default stands).
export function matchStoredDevice(
  stored: StoredDevice | undefined,
  devices: MediaDeviceInfo[],
): string | undefined {
  if (!stored) return undefined
  const byId = devices.find((d) => d.deviceId === stored.deviceId)
  if (byId) return byId.deviceId
  if (!stored.label) return undefined
  return devices.find((d) => d.label === stored.label)?.deviceId
}

// The one chokepoint for "make the call use the remembered devices": the
// pre-join screen and every (re)join both route through here, so a rejoin
// can't quietly fall back to the wrong camera (#940).
export async function applyStoredDevicePreferences(daily: DailyCall): Promise<void> {
  const prefs = loadDevicePreferences()
  if (!prefs.camera && !prefs.microphone && !prefs.speaker) return
  try {
    const { devices } = await daily.enumerateDevices()
    const videoDeviceId = matchStoredDevice(prefs.camera, devices.filter((d) => d.kind === "videoinput"))
    const audioDeviceId = matchStoredDevice(prefs.microphone, devices.filter((d) => d.kind === "audioinput"))
    const outputDeviceId = matchStoredDevice(prefs.speaker, devices.filter((d) => d.kind === "audiooutput"))
    if (videoDeviceId || audioDeviceId) {
      await daily.setInputDevicesAsync({
        ...(videoDeviceId ? { videoDeviceId } : {}),
        ...(audioDeviceId ? { audioDeviceId } : {}),
      })
    }
    if (outputDeviceId) {
      await daily.setOutputDeviceAsync({ outputDeviceId })
    }
  } catch {
    // A vanished device or a browser refusing enumeration must never block
    // joining — the browser default is the fallback.
  }
}
